/**
 * The reaction row under a bubble: the pills, the expander, then the add button — in that
 * order, left to right, for an own message and an incoming one alike. The row used to
 * reverse itself for the viewer's own messages; it does not, because a reader should not
 * have to learn where a control went based on who sent the message.
 *
 * ## It is IN FLOW, and that is the point
 *
 * This row used to be an absolutely positioned overlay hanging off the bubble's bottom
 * edge, which could hold exactly one line and covered the message below the moment it held
 * two. Here it is a normal sibling of the bubble, so its height IS the layout's: showing
 * more reactions pushes the thread down instead of painting over it.
 *
 * ## One flex container, one wrap
 *
 * The pills, the expander and the add button are all items of the SAME `flex-wrap` row, at
 * the full width of `BubbleCluster` — which hugs the bubble, so the row expands and shrinks
 * with it. An expanded row therefore ends with the expander and the add button on its last
 * line rather than parked outside it, and a row can never run off the side of the bubble it
 * belongs to.
 *
 * The row contributes nothing to that box's width (`BubbleFooter`'s `contain: inline-size`),
 * which is what keeps the arrangement from being circular: the row reads a width the BUBBLE
 * decided, never one the row's own last answer decided. The one thing that does widen the
 * box is {@link REACTION_ROW_MIN_WIDTH}, a constant.
 *
 * Nothing holds a reserved box: the expander is `display: none` while there is nothing to
 * expand, which is what keeps the add button flush against the last pill (a `w-0` placeholder
 * still spent the row's gap on either side of itself).
 *
 * ## Collapsed means "render one line", not "clip one line"
 *
 * The collapsed row renders only the pills that fit beside the expander and the add button,
 * so those two are always on the first line and always reachable. Clipping to one line
 * instead would have hidden them the moment the pills alone filled it.
 *
 * How many fit is measured, because CSS cannot answer it. The measurement runs on the row
 * as it would be with EVERYTHING shown — a `data-measuring` attribute reveals the hidden
 * pills for the duration of one synchronous read — so its inputs never depend on its own
 * last answer, and it cannot oscillate. It runs on mount, when the reactions change, and
 * when a `ResizeObserver` reports the row's width changed; `visibleCount` is only written
 * when it actually moves, so dragging the thread panel across a range that does not change
 * the answer re-renders nothing.
 */

import { cn } from "cn";
import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from "react";

import { BubbleFooter } from "@/components/ui/conversation";
import { BwEmojiSmilingAddLine } from "@/components/ui/icons";
import { Typography } from "@/components/ui/typography";
import type {
  Channel,
  ChannelMessage,
  ChannelMessageReaction,
} from "../_api/channel-endpoints";
import type { ToggleReaction } from "../_api/use-channel-reactions";
import {
  canReactInChannel,
  describeReactors,
  hasViewerReacted,
} from "../_libs/message-reaction";
import { ReactionPickerPopover } from "./channel-message-reaction-picker";
import {
  buttonPillClassName,
  ReactionPill,
} from "./channel-message-reaction-pill";

/** The row's `gap-1`, in px. The fit arithmetic has to spend it explicitly. */
const ROW_GAP = 4;

/**
 * The floor under the row's width. Worn by `BubbleCluster` — the box the row stretches to —
 * and only while the row is actually drawn (`channel-message-chrome`).
 *
 * A bubble hugs its message, and a message can be two characters long: "Hi" plus its
 * timestamp is about 90px, which fits one pill and nothing else. Without a floor the fit
 * arithmetic below answers 0, the row falls back to `+N more` and the add button, and the
 * reactions a message HAS stop being readable at all.
 *
 * 280px is where three pills, the expander and the add button all fit on the row's one
 * line, measured rather than guessed: 3 × 48.8px of pill (a two-digit count, the widest a
 * pill gets) + 62.4px expander + 34px add + 4 × {@link ROW_GAP} + the row's 2 × 8px inset =
 * 275px. Nothing re-derives it — the arithmetic below simply counts what fits.
 *
 * It is a MINIMUM, not a width: a bubble wider than this keeps its own width, so everything
 * above 280px stays responsive to the bubble. `min()` caps it at the budget — the
 * percentage resolves against `BubbleColumn`, the cluster's containing block — so a thread
 * panel narrower than the floor gets a row the width of its column and never a scrollbar.
 * Below the floor the arithmetic just counts fewer pills, exactly as it does above it.
 */
export const REACTION_ROW_MIN_WIDTH = "min-w-[min(17.5rem,100%)]";

/** Revealed during a measurement, hidden the rest of the time. */
const HIDDEN_WHILE_COLLAPSED = cn(
  /* sorted */
  "hidden",
  "group-data-[measuring]/reactions:flex",
);

type ChannelMessageReactionFooterProps = {
  channel: Channel;
  message: ChannelMessage;
  viewerId: string | undefined;
  /**
   * Which side the bubble sits on. It no longer moves anything — the row is laid out the
   * same way for both — and only picks the wash the pills borrow from that bubble.
   */
  align: "start" | "end";
  /** Already ranked and capped by `rankReactions`, with the viewer's pending clicks applied. */
  reactions: ChannelMessageReaction[];
  canReact: boolean;
  toggleReaction: ToggleReaction;
};

export function ChannelMessageReactionFooter({
  channel,
  message,
  viewerId,
  align,
  reactions,
  canReact,
  toggleReaction,
}: ChannelMessageReactionFooterProps) {
  const [isExpanded, setIsExpanded] = useState(false);
  const [visibleCount, setVisibleCount] = useState(reactions.length);
  const rowRef = useRef<HTMLDivElement>(null);

  /**
   * How many pills fit on one line beside the expander and the add button.
   *
   * Arithmetic, not positions: it adds up intrinsic widths against the BUDGET — the width
   * of `BubbleCluster`, which hugs the BUBBLE (down to its floor) and is deaf to this row —
   * so the answer is a function of the bubble's width and the pills' own widths, and of
   * nothing this component decided. Measuring the row's own right edge instead would have been measuring the
   * result of the last answer, and it latched: the row shrank to what it showed, the next
   * measurement saw that smaller row, and the count could never climb back.
   *
   * Two passes, because whether the expander is needed decides how much room is left for
   * pills, and that is the only circular part. Pass one asks whether everything fits with
   * no expander at all; only if it does not does pass two make room for one.
   */
  const measure = useCallback(() => {
    const row = rowRef.current;
    const budget = row?.parentElement;
    if (!row || !budget) return;

    // Fractional widths throughout: `offsetWidth` rounds to whole pixels, and summing
    // eight pills each rounded DOWN let a ninth through that did not fit.
    const widthOf = (selector: string) =>
      row.querySelector<HTMLElement>(selector)?.getBoundingClientRect().width ??
      0;

    row.setAttribute("data-measuring", "");
    const pills = [
      ...row.querySelectorAll<HTMLElement>('[data-test="message-reaction"]'),
    ].map((pill) => pill.getBoundingClientRect().width);
    const more = widthOf('[data-test="message-reaction-expand"]');
    const add = widthOf('[data-test="message-reaction-add"]');
    // The budget is a border box and the row spends some of it on its own inset, which is
    // not room the pills can use. Forgetting it bought one pill too many on every row.
    const inset = Number.parseFloat(getComputedStyle(row).paddingInline || "0");
    const available = budget.getBoundingClientRect().width - inset * 2;
    row.removeAttribute("data-measuring");

    const fit = (trailing: number) => {
      let used = trailing;
      let count = 0;
      for (const pill of pills) {
        if (used + pill + ROW_GAP > available) break;
        used += pill + ROW_GAP;
        count++;
      }
      return count;
    };

    const withoutMore = fit(add);
    const count =
      withoutMore === pills.length ? withoutMore : fit(add + more + ROW_GAP);

    setVisibleCount((previous) => (previous === count ? previous : count));
  }, []);

  // The BUDGET's width is what the fit depends on, so the observer watches that box rather
  // than the row or the window: a resizable thread panel, an aside opening, a window resize
  // and the bubble's own text reflowing are all the same event here, and the row's own size
  // — which this component controls — is deliberately not an input.
  useLayoutEffect(() => {
    const budget = rowRef.current?.parentElement;
    if (!budget) return;
    const observer = new ResizeObserver(measure);
    observer.observe(budget);
    return () => observer.disconnect();
  }, [measure]);

  // Re-measure when the pills themselves change: a reaction landing, a count gaining a
  // digit. The row's box can be identical across both, so the observer above does not fire.
  useEffect(measure, [
    measure,
    reactions.map((reaction) => `${reaction.type}:${reaction.count}`).join(),
  ]);

  const visible = isExpanded ? reactions : reactions.slice(0, visibleCount);
  const hiddenCount = reactions.length - visible.length;
  const canExpand = isExpanded || hiddenCount > 0;

  return (
    <BubbleFooter
      ref={rowRef}
      data-test="message-reactions"
      // One flex-wrap row holding everything, so the group wraps as a unit. `items-center`
      // rather than `items-start`: every item is the same 24px tall, and a wrapped line
      // should sit flush with the one above it.
      //
      // `justify-end` is the one thing that follows the bubble's side. The row is the
      // bubble's full width now, so without it an own message's single reaction would sit
      // at the far end of the bubble from everything else that message aligns — the row's
      // ORDER is still identical on both sides, only which edge it packs against moves.
      className={cn(
        /* sorted */
        "flex-wrap items-center",
        "group/reactions",
        align === "end" && "justify-end",
      )}
    >
      {reactions.map((reaction, index) => (
        <ReactionPill
          key={reaction.type}
          reaction={reaction}
          isReacted={hasViewerReacted(reaction, viewerId)}
          tooltip={describeReactors(reaction, viewerId)}
          align={align}
          // Not disabled while a write is in flight: the viewer's pending intent already
          // shows the outcome of the click (`useChannelReactions`), so a second click is
          // a second real toggle rather than a double-submit.
          disabled={!canReactInChannel(channel)}
          onToggle={() => toggleReaction(message, reaction.type)}
          className={
            index < visible.length ? undefined : HIDDEN_WHILE_COLLAPSED
          }
        />
      ))}

      {/* Always in the DOM so the fit arithmetic can always count its width, and
          `display: none` rather than a zero-width box when there is nothing to expand —
          a collapsed placeholder still spent a gap on either side of itself, which is
          the stray space that used to sit between the last pill and the add button. */}
      <button
        type="button"
        data-test="message-reaction-expand"
        data-reaction-trailing=""
        aria-expanded={isExpanded}
        aria-label={
          isExpanded
            ? "Show fewer reactions"
            : `Show ${hiddenCount} more reactions`
        }
        onClick={() => setIsExpanded((open) => !open)}
        className={cn(
          /* sorted */
          "flex",
          "shrink-0 items-center justify-center",
          "px-2",
          "cursor-pointer",
          buttonPillClassName(align, { pop: false }),
          canExpand ? undefined : HIDDEN_WHILE_COLLAPSED,
        )}
      >
        <Typography size="xs" className="text-text-dark-high">
          {isExpanded ? "Show less" : `+${hiddenCount} more`}
        </Typography>
      </button>

      {canReact && (
        // `align="end"` for every message, like the bar's copy of this button: the add
        // button ends the row on both sides, so a picker opening from its right edge is
        // the one that always has room to its left.
        <ReactionPickerPopover
          align="end"
          tooltip="Add reaction"
          onPick={(type) => toggleReaction(message, type)}
        >
          {/* A plain button rather than `Button`: every size variant there is a square
              (`icon-xs` is `h-6 w-6`), and Figma's `Emoji / Add` is a glyph in a `px-2`
              capsule — the same shape as the pills beside it. The glyph is 16px, like the
              action bar's copy of this button: 20px in a 24px capsule leaves 1px above and
              below the border. */}
          <button
            type="button"
            aria-label="Add reaction"
            data-test="message-reaction-add"
            data-reaction-trailing=""
            className={cn(
              /* sorted */
              "flex",
              "items-center justify-center",
              "px-2",
              "cursor-pointer",
              buttonPillClassName(align),
            )}
          >
            <BwEmojiSmilingAddLine className="size-4" />
          </button>
        </ReactionPickerPopover>
      )}
    </BubbleFooter>
  );
}
